import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface DrizzleEffectProps {
  intensity?: number;
  particleCount?: number;
  areaSize?: [number, number];
  color?: string;
}

export function DrizzleEffect({
  intensity = 0.3,
  particleCount = 300,
  areaSize = [20, 20],
  color = '#b0c4de',
}: DrizzleEffectProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  // Initialize particle positions
  const particles = useMemo(() => {
    return Array.from({ length: particleCount }, () => ({
      position: new THREE.Vector3(
        Math.random() * areaSize[0],
        Math.random() * 8 + 2,
        Math.random() * areaSize[1]
      ),
      speed: 2 + Math.random() * 1.5,
      offset: Math.random() * Math.PI * 2,
    }));
  }, [particleCount, areaSize]);

  useFrame((state, delta) => {
    if (!meshRef.current) return;

    const time = state.clock.elapsedTime;

    particles.forEach((particle, i) => {
      // Gentle falling with slight wind sway
      particle.position.y -= particle.speed * delta * intensity * 3;
      particle.position.x += Math.sin(time * 0.5 + particle.offset) * 0.01;

      // Reset when below ground
      if (particle.position.y < 0) {
        particle.position.y = 8 + Math.random() * 2;
        particle.position.x = Math.random() * areaSize[0];
        particle.position.z = Math.random() * areaSize[1];
      }

      dummy.position.copy(particle.position);
      dummy.scale.setScalar(0.015 + Math.random() * 0.01);
      dummy.updateMatrix();
      meshRef.current!.setMatrixAt(i, dummy.matrix);
    });

    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, particleCount]}>
      <sphereGeometry args={[1, 4, 4]} />
      <meshBasicMaterial
        color={color}
        transparent
        opacity={0.4 * intensity}
      />
    </instancedMesh>
  );
}
